/**
 * Environment configuration types module
 * Types for application environment configuration
 */

/**
 * Environment configuration
 * @property {boolean} production - Whether in production mode
 * @property {string} apiUrl - Backend API URL
 * @property {string} name - Environment name
 * @property {number} [backendTimeout] - Backend request timeout in ms
 * @property {boolean} [devAuthOff] - Whether to disable auth in dev mode
 * @property {Object} azure - Azure AD configuration
 * @property {string} azure.clientId - Azure AD client ID
 * @property {string} azure.authority - Azure AD authority URL
 * @property {string} azure.redirectUri - OAuth redirect URI
 * @property {string[]} azure.scopes - API access scopes
 */
export interface Environment {
  production: boolean;
  apiUrl: string;
  name: string;
  backendTimeout?: number;
  devAuthOff?: boolean;
  azure: {
    clientId: string;
    authority: string;
    redirectUri: string;
    scopes: string[];
  };
}

/**
 * Validates environment configuration
 * Checks required fields and Azure configuration
 * @param {Environment} env - Environment to validate
 * @throws {Error} If validation fails
 */
export function validateEnvironment(env: Environment): void {
  const errors: string[] = [];

  if (!env.apiUrl) errors.push('apiUrl is required');
  if (!env.name) errors.push('name is required');
  
  // Skip Azure validation if dev auth is off
  if (!env.devAuthOff) {
    if (!env.azure) {
      errors.push('azure configuration is required');
    } else {
      if (!env.azure.clientId) errors.push('azure.clientId is required');
      if (!env.azure.authority) errors.push('azure.authority is required');
      if (!env.azure.redirectUri) errors.push('azure.redirectUri is required');
      if (!env.azure.scopes || env.azure.scopes.length === 0) errors.push('azure.scopes is required');
    }
  }

  if (errors.length > 0) {
    throw new Error(`Environment validation failed:\n${errors.join('\n')}`);
  }
}
